import { OrderItemDocument } from '@models/order';
import { ProductDocument, Variation } from '@models/product';
import { MAKE_ARRAY } from 'models/shared';

export type SelectedBike = {
  year?: number | null;
  make?: string | null;
  model?: string | null;
};

export function formatSelectedBike(
  bike?: SelectedBike | null
): string | undefined {
  const label = [bike?.year, bike?.make, bike?.model].filter(Boolean).join(' ');
  return label || undefined;
}

// a label only carries a year when the vehicle had one, and the make always
// follows it
const YEAR_PREFIX = /^(?:19|20)\d{2}(?=\s|$)/;

// the reverse of `formatSelectedBike`, for the label stored on a cart item.
// Returns null when the label carries no recognizable make, since without one
// there is no telling where the model starts
export function parseSelectedBikeLabel(
  label?: string | null
): SelectedBike | null {
  const trimmed = label?.trim();
  if (!trimmed) return null;

  const [yearMatch] = YEAR_PREFIX.exec(trimmed) ?? [];
  const rest = yearMatch ? trimmed.slice(yearMatch.length).trim() : trimmed;
  const lowered = rest.toLowerCase();

  // "Moto Guzzi" and "Moto Morini" share a first word, so the longest make the
  // label starts with wins
  const make = MAKE_ARRAY.filter(
    (candidate) =>
      lowered === candidate.toLowerCase() ||
      lowered.startsWith(`${candidate.toLowerCase()} `)
  ).sort((a, b) => b.length - a.length)[0];

  if (!make) return null;

  const model = rest.slice(make.length).trim();

  return {
    year: yearMatch ? Number(yearMatch) : null,
    make,
    model: model || null,
  };
}

// stripe caps a metadata value at 500 characters
const BIKES_METADATA_LIMIT = 500;

// the vehicles a buyer had selected for the items they are checking out with,
// for the note the webhook writes on the order
export function formatOrderedForBikes(
  items: { bike?: string }[]
): string | undefined {
  const labels = Array.from(
    new Set(items.map((item) => item.bike).filter((bike) => !!bike))
  );
  if (!labels.length) return undefined;
  const joined = labels.join(', ');
  return joined.length > BIKES_METADATA_LIMIT
    ? `${joined.slice(0, BIKES_METADATA_LIMIT - 1)}…`
    : joined;
}

export function findOrderItemVariation(
  item: OrderItemDocument,
  product?: ProductDocument | null
): Variation | undefined {
  if (!product?.variations?.length) return undefined;
  if (!Object.keys(item.variations ?? {}).length) return undefined;
  return product.variations.find(
    (v) =>
      (!item.variations?.letter || v.size === item.variations.letter) &&
      (!item.variations?.number || v.size === item.variations.number) &&
      (!item.variations?.color || v.color === item.variations.color)
  );
}

// the part numbers of the ordered variation, else the listing's own
export function getOrderItemPartNumbers(
  item: OrderItemDocument,
  product?: ProductDocument | null
): { sku?: string; mpn?: string } {
  const variation = findOrderItemVariation(item, product);
  return {
    sku: variation?.sku ?? product?.sku,
    mpn: variation?.mpn?.[0] ?? product?.mpn?.[0],
  };
}

// the image of the ordered variation, else the listing's own. `thumbnail` is
// empty on listings created from the web, so the gallery is the reliable source
export function getOrderItemImage(
  item: OrderItemDocument,
  product?: ProductDocument | null
): string | undefined {
  const variation = findOrderItemVariation(item, product);
  return (
    variation?.image ||
    product?.images?.[0]?.thumb ||
    product?.images?.[0]?.full ||
    product?.thumbnail ||
    undefined
  );
}
